/* eslint-disable react/no-array-index-key */
import { Button } from '@efexteam/components';
import useStakeholderFileService from '@efex-components/EfexDS/FileInput/useStakeholderFileService';
import Autorenew from '@mui/icons-material/Autorenew';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { Alert, Box, Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiCall } from 'use-api-call';
import { selectBusiness } from '@pages/profile/store/selectors';
import { useSelector } from 'react-redux';
import clientSvc from '../../../../../services/client/clientSvc';
import { IRawDocumentsOnboarding } from '../../../../../services/client/types';
import { useFormData } from '../../context';
import { MexicoStakeHoldersFormData } from '../../types/stakeholders.type';
import StakeHolderFormMX from '../mex/StakeHolder.Form';
import StakeHolderProvider from './context/StakeHolderProvider';
import { MAX_STAKEHOLDERS } from './stakeholders.constants';

interface IStakeHoldersForm {
  isDisabledEdit?: boolean;
  loading?: boolean;
  nextFormStep: (data: IRawDocumentsOnboarding) => void;
  prevStep: () => void;
}

const StakeHolders = ({
  isDisabledEdit = false,
  loading: loadingOnboarding,
  nextFormStep,
  prevStep,
}: IStakeHoldersForm) => {
  const { t } = useTranslation();

  const {
    data,
    stkPartials,
    getStakeHolder,
    countries,
    birthcountries,
    federalEntities,
    getCountries,
    getFederalEntities,
    getBirthCountries,
  } = useFormData();

  const { resetUploadedFiles } = useStakeholderFileService({
    getStakeHolder,
  });

  const {
    error: errorDelete,
    invoke: deleteStakeHolder,
    data: deleteStakeHolderData,
  } = useApiCall((id: string | number) => clientSvc.deleteStakeHolder(id));

  const [stakeHolders, setStakeHolders] = useState<
    MexicoStakeHoldersFormData[]
  >([]);

  const company = useSelector(selectBusiness);

  const onSubmit = async () => {
    nextFormStep({
      ...data,
      isLastStep: true,
      isStakeHolderDataConfirm: undefined,
    });
  };

  const handleBack = () => {
    prevStep();
  };

  const addNewStakeHolder = () => {
    resetUploadedFiles();
    const newData = [...stakeHolders, {} as MexicoStakeHoldersFormData];
    setStakeHolders(newData);
  };

  const removeStakeHolder = async (index: number) => {
    const isExisting = (stkPartials as MexicoStakeHoldersFormData[]).find(
      (record) => record.id === index
    );
    if (isExisting) {
      deleteStakeHolder(index);
      return;
    }
    const newData = [...stakeHolders];
    newData.splice(index, 1);
    setStakeHolders(newData);
  };

  useEffect(() => {
    if (stkPartials) {
      setStakeHolders([]);
      setTimeout(() => {
        setStakeHolders(stkPartials);
      }, 100);
    }
  }, [stkPartials]);

  useEffect(() => {
    if (deleteStakeHolderData) {
      getStakeHolder({ enterpriseId: company?.enterpriseId ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStakeHolderData]);

  const renderForms = () =>
    stakeHolders.map((form, index) => (
      <div className="max-w-[1000px] mx-auto" key={index}>
        <StakeHolderProvider>
          <StakeHolderFormMX
            index={form.id ?? index}
            remove={removeStakeHolder}
            data={form}
            countries={countries}
            birthcountries={birthcountries}
            federalEntities={federalEntities}
            getCountries={getCountries}
            getFederalEntities={getFederalEntities}
            getBirthCountries={getBirthCountries}
            stakeholderId={form.id}
            getStakeHolder={getStakeHolder}
          />
        </StakeHolderProvider>
      </div>
    ));

  const userHaveAtLeastOneStakeholder = useMemo(
    () =>
      stakeHolders &&
      stakeHolders[0] &&
      stakeHolders[0].name &&
      stakeHolders[0].name?.length > 0,
    [stakeHolders]
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <div className="max-w-[1000px] mx-auto">
          <Typography variant="h4" className="text-left !font-medium">
            {t('onbStakeHoldersTitle')}
          </Typography>
          {!isDisabledEdit ? (
            <Box className="flex justify-end pt-6 pb-3">
              <Button
                onClick={addNewStakeHolder}
                appearance={
                  !userHaveAtLeastOneStakeholder ? 'primary' : 'secondary'
                }
                className={clsx(
                  'ml-auto mb-3 mt-5',
                  !userHaveAtLeastOneStakeholder ? 'animate-pulse' : ''
                )}
                endIcon={<PersonAddAltIcon />}
                disabled={stakeHolders.length >= MAX_STAKEHOLDERS}
              >
                {t('onbAdd')}{' '}
              </Button>
            </Box>
          ) : null}

          <div className="flex flex-col gap-3">
            <Alert severity="info">{t('onbAddOneStakeHolder')}</Alert>
            <Alert severity="info">{t('infoStakeholders')}</Alert>
          </div>

          {errorDelete ? (
            <Alert className="!my-5" severity="error">
              {t('onbStkCreateError')}
            </Alert>
          ) : null}
          {renderForms()}
        </div>
      </Grid>

      <Grid item xs={12}>
        <div className="flex justify-around !mt-5 max-w-[1000px] mx-auto">
          {!isDisabledEdit ? (
            <>
              <Button
                size="large"
                className="mt-2 text-[14px]"
                type="button"
                appearance="secondary"
                onClick={handleBack}
              >
                {t('backBtn')}
              </Button>
              {stakeHolders &&
                stakeHolders[0] &&
                stakeHolders[0].name &&
                stakeHolders[0].name?.length > 0 && (
                  <Button
                    appearance="primary"
                    size="large"
                    className="mt-2 text-[14px]"
                    type="button"
                    onClick={onSubmit}
                    disabled={
                      !(
                        stakeHolders &&
                        stakeHolders[0] &&
                        stakeHolders[0].name &&
                        stakeHolders[0].name?.length > 0
                      ) || loadingOnboarding
                    }
                    endIcon={
                      !loadingOnboarding ? undefined : (
                        <Autorenew className="animate-spin" />
                      )
                    }
                  >
                    {t('btnCompleteRegister')}
                  </Button>
                )}
            </>
          ) : (
            <Alert severity="info">{t('kybStepTwoTitle')}</Alert>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default StakeHolders;
